import type {
  BlackAndWhiteViewerState,
  TileNumber,
} from "@cloudnest/black-and-white-engine";
import { initGame } from "@cloudnest/black-and-white-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

// Canonical live routes submit/read via the game-platform API
// (/api/v2/game-platform/...) which calls the pinned black-and-white rule
// service; legacy-only callers still use the old room API adapter.
function resolveBlackAndWhiteGameApiMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("black-and-white", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveBlackAndWhiteGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("black-and-white", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

export interface BlackAndWhiteMultiplayerState {
  readonly viewerState: BlackAndWhiteViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

const INITIAL_STATE: BlackAndWhiteMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
};

let state: BlackAndWhiteMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }

  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as BlackAndWhiteViewerState,
      lastAppliedSeq: bestSeq,
    };
  }

  return bestSeq;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: {
      error: error instanceof Error ? error.message : fallback,
    },
  };
  notify();
}

export const blackAndWhiteMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    hostPlayerId: string,
    firstPlayerId?: string,
    observerGodViewEnabled = false,
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      connection: "connecting",
    };
    notify();

    try {
      const existing = await getGameApi().getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatest(existingRows, 0);
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      const initialState = initGame({
        gameId: roomId,
        playerIds,
        hostPlayerId,
        firstPlayerId,
        observerGodViewEnabled,
      });
      await getGameApi().submitEvent(roomId, "init", { state: initialState });
      await getGameApi().submitEvent(roomId, "SyncViewerState", {
        actionId: `${roomId}:sync-initial-view`,
      });

      const projected = await getGameApi().getViewerStates(roomId);
      if (projected.success && projected.data) {
        adoptLatest(projected.data, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_BLACK_AND_WHITE_INIT_FAILED");
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      connection: "connecting",
    };
    notify();

    try {
      const res = await getGameApi().getViewerStates(roomId);
      if (res.success && res.data && state.roomId === roomId) {
        adoptLatest(res.data, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_BLACK_AND_WHITE_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const res = await getGameApi().getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (res.success && res.data) {
        adoptLatest(res.data, state.lastAppliedSeq);
        notify();
      }
    } catch (error) {
      setError(error, "ERR_BLACK_AND_WHITE_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as BlackAndWhiteViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  submitTile: async (
    tile: TileNumber,
  ): Promise<{ readonly success: boolean; readonly error?: string }> => {
    const roomId = state.roomId;
    if (!roomId) return { success: false, error: "ERR_NO_GAME" };

    try {
      const res = await getGameApi().submitEvent(roomId, "SubmitTile", {
        tile,
      });
      if (!res.success) {
        const error = res.error ?? "ERR_BLACK_AND_WHITE_ACTION";
        state = { ...state, lastError: { error } };
        notify();
        return { success: false, error };
      }

      const viewerStates = await getGameApi().getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (
        viewerStates.success &&
        viewerStates.data &&
        state.roomId === roomId
      ) {
        adoptLatest(viewerStates.data, state.lastAppliedSeq);
      }
      if (state.roomId !== roomId) return { success: true };
      state = { ...state, lastError: null };
      notify();
      return { success: true };
    } catch (error) {
      setError(error, "ERR_BLACK_AND_WHITE_ACTION");
      return { success: false, error: "ERR_BLACK_AND_WHITE_ACTION" };
    }
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export function useBlackAndWhiteMultiplayerStore(): BlackAndWhiteMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
